import { getPublicKey } from "nostr-tools/pure";
import { decrypt, encrypt, getConversationKey } from "nostr-tools/nip44";

/**
 * The member's address book (PLAN.md 4.14): named groups of followed members and an alias per
 * member, kept on the server as one NIP-44 payload encrypted to the member's own key. The server
 * stores it and never reads it: group names such as "doctor" say more than a follow list.
 *
 * Members are named by their member id (stable when they change keys). A member can be in many
 * groups; a group can hold members the owner no longer follows (the app shows them as such).
 */

export const ADDRESS_BOOK_VERSION = 1;
export const ADDRESS_BOOK_LIMITS = { groups: 50, membersPerGroup: 500, aliases: 500, groupName: 40, alias: 60 } as const;
/** NIP-44 v2 takes at most 65 535 bytes of plaintext. */
const MAX_PLAINTEXT = 65_000;

export type AddressGroup = { id: string; name: string; members: number[] };
export type AddressBook = { v: 1; groups: AddressGroup[]; aliases: Record<string, string> };

export class AddressBookError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AddressBookError";
  }
}

export const emptyAddressBook = (): AddressBook => ({ v: 1, groups: [], aliases: {} });

/** One line, no control characters, trimmed, at most `max` characters. */
function cleanText(value: unknown, max: number): string {
  if (typeof value !== "string") throw new AddressBookError("expected text");
  const s = value.replace(/\p{Cc}/gu, " ").replace(/\s+/g, " ").trim();
  if ([...s].length > max) throw new AddressBookError(`text longer than ${max} characters`);
  return s;
}

const isMemberId = (n: unknown): n is number => typeof n === "number" && Number.isSafeInteger(n) && n > 0;

/** Validates and normalizes any input (also what a newer app or a damaged blob may hold). */
export function normalizeAddressBook(input: unknown): AddressBook {
  const raw = input as Partial<AddressBook> | null;
  if (!raw || typeof raw !== "object" || raw.v !== ADDRESS_BOOK_VERSION) throw new AddressBookError("not an address book v1");
  if (!Array.isArray(raw.groups) || raw.groups.length > ADDRESS_BOOK_LIMITS.groups) throw new AddressBookError(`at most ${ADDRESS_BOOK_LIMITS.groups} groups`);
  const ids = new Set<string>();
  const groups = raw.groups.map((g) => {
    if (!g || typeof g.id !== "string" || !/^[A-Za-z0-9_-]{1,32}$/.test(g.id) || ids.has(g.id)) throw new AddressBookError("bad group id");
    ids.add(g.id);
    const name = cleanText(g.name, ADDRESS_BOOK_LIMITS.groupName);
    if (!name) throw new AddressBookError("a group needs a name");
    if (!Array.isArray(g.members) || !g.members.every(isMemberId)) throw new AddressBookError("group members are member ids");
    const members = [...new Set(g.members)];
    if (members.length > ADDRESS_BOOK_LIMITS.membersPerGroup) throw new AddressBookError(`at most ${ADDRESS_BOOK_LIMITS.membersPerGroup} members per group`);
    return { id: g.id, name, members };
  });
  const aliasEntries = Object.entries(raw.aliases ?? {});
  if (aliasEntries.length > ADDRESS_BOOK_LIMITS.aliases) throw new AddressBookError(`at most ${ADDRESS_BOOK_LIMITS.aliases} aliases`);
  const aliases: Record<string, string> = {};
  for (const [id, alias] of aliasEntries) {
    if (!isMemberId(Number(id)) || String(Number(id)) !== id) throw new AddressBookError("aliases are keyed by member id");
    const text = cleanText(alias, ADDRESS_BOOK_LIMITS.alias);
    if (text) aliases[id] = text;
  }
  return { v: 1, groups, aliases };
}

/** Encrypts the book to the owner's own key (NIP-44 v2, conversation key of the key with itself). */
export function sealAddressBook(book: AddressBook, secretKey: Uint8Array): string {
  const json = JSON.stringify(normalizeAddressBook(book));
  if (new TextEncoder().encode(json).length > MAX_PLAINTEXT) throw new AddressBookError("the address book is too large");
  return encrypt(json, getConversationKey(secretKey, getPublicKey(secretKey)));
}

export function openAddressBook(payload: string, secretKey: Uint8Array): AddressBook {
  let json: string;
  try {
    json = decrypt(payload, getConversationKey(secretKey, getPublicKey(secretKey)));
  } catch {
    throw new AddressBookError("the address book cannot be opened with this key");
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new AddressBookError("the address book is damaged");
  }
  return normalizeAddressBook(parsed);
}

/** A new group id: 12 random base64url characters. */
export function newGroupId(): string {
  const bytes = (globalThis as { crypto?: Crypto }).crypto!.getRandomValues(new Uint8Array(9));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_");
}
